/**
 * API utility functions for consistent API handling across the application
 */

import axios from 'axios';

/**
 * Base API configuration with environment support
 */
export const API_BASE_URL = process.env.REACT_APP_API_URL || 
  (process.env.NODE_ENV === 'production' 
    ? 'https://extensync.onrender.com/api' 
    : 'http://localhost:5000/api');

// Base host for non-/api assets and socket connections
export const API_HOST_BASE = API_BASE_URL.replace(/\/api$/, '');

/**
 * Get image URL for different types of images
 * @param {string} imagePath - The image path from database
 * @param {string} type - Type of image (profile-photos, signatures, report-photos)
 * @returns {string} Full image URL
 */
export const getImageUrl = (imagePath, type = 'profile-photos') => {
  if (!imagePath) return null;
  
  // If it's already a full URL, return as is
  if (imagePath.startsWith('http')) {
    return imagePath;
  }
  
  // Extract filename from path
  const filename = imagePath.split('/').pop();
  
  // Use the new API endpoint for better error handling
  return `${API_BASE_URL}/image/${type}/${filename}`;
};

/**
 * Create axios instance with default configuration
 */
export const apiClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000, // Increased to 30 seconds for slower operations and server wake-up
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to handle FormData - remove Content-Type header so axios can set it automatically
apiClient.interceptors.request.use((config) => {
  // If the data is FormData, remove Content-Type header so axios can set it with the correct boundary
  if (config.data instanceof FormData) {
    delete config.headers['Content-Type'];
  }
  return config;
}, (error) => {
  return Promise.reject(error);
});

/**
 * Set authorization token for API requests
 * @param {string} token - JWT token
 */
export const setAuthToken = (token) => {
  console.log('setAuthToken called with:', token ? 'token present' : 'no token');
  if (token) {
    apiClient.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    console.log('Authorization header set:', apiClient.defaults.headers.common['Authorization']);
  } else {
    delete apiClient.defaults.headers.common['Authorization'];
    console.log('Authorization header cleared');
  }
};

/**
 * Set last activity timestamp for inactivity tracking
 * @param {number} timestamp - Last activity timestamp
 */
export const setLastActivity = (timestamp) => {
  if (timestamp) {
    apiClient.defaults.headers.common['X-Last-Activity'] = timestamp.toString();
  } else {
    delete apiClient.defaults.headers.common['X-Last-Activity'];
  }
};

/**
 * Handle API errors consistently
 * @param {Error} error - The error object
 * @param {string} context - Context for error logging
 * @returns {Object} Standardized error object
 */
export const handleApiError = (error, context = 'API call') => {
  console.error(`${context} failed:`, error);
  
  if (error.response) {
    // Server responded with error status
    return {
      message: error.response.data?.error || error.response.data?.message || 'Server error occurred',
      status: error.response.status,
      data: error.response.data
    };
  } else if (error.request) {
    // Request was made but no response received
    return {
      message: 'Network error - please check your connection',
      status: 0,
      data: null
    };
  } else {
    // Something else happened
    return {
      message: error.message || 'An unexpected error occurred',
      status: 0,
      data: null
    };
  }
};

/**
 * Standard API response handler
 * @param {Function} apiCall - The API function to call
 * @param {string} context - Context for error handling
 * @returns {Promise<Object>} Standardized response
 */
export const handleApiResponse = async (apiCall, context = 'API call') => {
  try {
    const response = await apiCall();
    return {
      success: true,
      data: response.data,
      status: response.status
    };
  } catch (error) {
    return {
      success: false,
      error: handleApiError(error, context),
      data: null
    };
  }
};

/**
 * Common API endpoints
 */
export const API_ENDPOINTS = {
  // Authentication
  LOGIN: '/auth/login',
  BENEFICIARY_LOGIN: '/auth/beneficiary-login',
  BENEFICIARY_REGISTER: '/auth/beneficiary-register',
  
  // Users
  USERS: '/users',
  USER_PROFILE: '/users/profile',
  
  // Projects
  PROJECTS: '/projects',
  PROJECT_STATS: '/projects/stats/dashboard',
  
  // Work Plans
  WORK_PLANS: '/work-plans',
  WORK_PLAN_TEMPLATE: '/work-plans/generate-template',
  
  // Evaluations
  EVALUATIONS: '/evaluations',
  REPORTS: '/reports',
  
  // Documents
  DOCUMENTS: '/documents',
  
  // Notifications
  NOTIFICATIONS: '/notifications',
  
  // Community
  COMMUNITY: '/community'
};

/**
 * Build query string from parameters
 * @param {Object} params - Query parameters
 * @returns {string} Query string
 */
export const buildQueryString = (params) => {
  const searchParams = new URLSearchParams();
  
  Object.entries(params).forEach(([key, value]) => {
    if (value !== null && value !== undefined && value !== '') {
      searchParams.append(key, value);
    }
  });
  
  return searchParams.toString();
};

/**
 * Format date for API requests
 * @param {Date|string} date - Date to format
 * @returns {string} Formatted date string
 */
export const formatDateForApi = (date) => {
  if (!date) return null;
  
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  return dateObj.toISOString().split('T')[0];
};

/**
 * Format datetime for API requests
 * @param {Date|string} date - Date to format
 * @returns {string} Formatted datetime string
 */
export const formatDateTimeForApi = (date) => {
  if (!date) return null;
  
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  return dateObj.toISOString();
};
